import React, { useState, useEffect, useCallback } from 'react';
import {
  getEvents,
  getRegistrations, getRegistrationCount, getRegistrationCountByType,
  cancelRegistration, promoteFromWaitlist,
  manualAddRegistrant, overrideCapacity,
  exportRegistrants,
  manualCheckIn,
  getQRCodeUrl,
  fmtDateTime,
} from '../../../api/eventsApi';

const TABS = [
  { key: 'selectEvent',  label: 'Select Event' },
  { key: 'registrations', label: 'Registrations' },
  { key: 'waitlist',     label: 'Waitlist' },
  { key: 'manualAdd',    label: 'Manual Add' },
];

const STATUS_BADGE = {
  CONFIRMED:  'bb-badge bb-badge--green',
  WAITLISTED: 'bb-badge bb-badge--orange',
  CANCELLED:  'bb-badge bb-badge--red',
  CHECKED_IN: 'bb-badge bb-badge--blue',
  NO_SHOW:    'bb-badge bb-badge--grey',
};

function TabBar({ activeTab, onSelect }) {
  return (
    <div className="bb-prog-tabs">
      {TABS.map(t => (
        <button
          key={t.key}
          onClick={() => onSelect(t.key)}
          className={`bb-prog-tab${activeTab === t.key ? ' active' : ''}`}
          style={t.key === 'selectEvent' ? { paddingLeft: '3ch' } : undefined}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export default function RegistrationWaitlist({ orgId, adminId, adminName }) {
  const [activeTab, setActiveTab] = useState('selectEvent');

  // Event selection
  const [allEvents, setAllEvents]         = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [counts, setCounts]               = useState({ confirmed: 0, waitlisted: 0 });

  // Registrations
  const [registrations, setRegistrations]   = useState([]);
  const [regsLoading, setRegsLoading]       = useState(false);

  // Modals
  const [showCancelModal, setShowCancelModal]     = useState(false);
  const [cancelTarget, setCancelTarget]           = useState(null);
  const [cancelReason, setCancelReason]           = useState('');
  const [showQRModal, setShowQRModal]             = useState(false);
  const [qrTarget, setQrTarget]                   = useState(null);
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [newCapacity, setNewCapacity]             = useState('');
  const [capacityReason, setCapacityReason]       = useState('');
  const [showPromoteModal, setShowPromoteModal]   = useState(false);
  const [promoteTarget, setPromoteTarget]         = useState(null);
  const [promoteReason, setPromoteReason]         = useState('');

  // Manual add form
  const [manualForm, setManualForm] = useState({ memberId: '', memberName: '', memberEmail: '', note: '' });

  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');

  const showSuccessMsg = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };
  const showErrorMsg   = (msg) => { setError(msg);   setTimeout(() => setError(''), 5000); };

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const all = await getEvents(orgId);
      setAllEvents(all.filter(e => e.status === 'PUBLISHED' || e.status === 'COMPLETED'));
    } catch (e) {
      showErrorMsg(e.message || 'Failed to load events.');
    } finally {
      setEventsLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const [typeCounts, setTypeCounts] = useState({ member_confirmed: 0, member_waitlisted: 0, volunteer_confirmed: 0, volunteer_waitlisted: 0 });

  const loadRegistrations = useCallback(async (eventId) => {
    setRegsLoading(true);
    try {
      const [regs, cnts, tCounts] = await Promise.all([
        getRegistrations(orgId, eventId),
        getRegistrationCount(orgId, eventId),
        getRegistrationCountByType(orgId, eventId),
      ]);
      setRegistrations(regs || []);
      setCounts(cnts);
      setTypeCounts(tCounts);
    } catch (e) {
      showErrorMsg(e.message || 'Failed to load registrations.');
    } finally {
      setRegsLoading(false);
    }
  }, [orgId]);

  const handleLoadRegistrations = async () => {
    if (!selectedEvent) return;
    await loadRegistrations(selectedEvent.id);
    setActiveTab('registrations');
  };

  // ── Actions ───────────────────────────────────────────────────────────────────
  const handleCheckIn = async (reg) => {
    setActionLoading(true);
    try {
      await manualCheckIn(orgId, selectedEvent.id, reg.id, adminId, 'Manual check-in by admin');
      showSuccessMsg(`${reg.member_name} checked in.`);
      await loadRegistrations(selectedEvent.id);
    } catch (e) {
      showErrorMsg(e.message || 'Check-in failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelReg = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    setActionLoading(true);
    try {
      await cancelRegistration(orgId, selectedEvent.id, cancelTarget.id, cancelReason, adminId);
      showSuccessMsg('Registration cancelled.');
      setShowCancelModal(false);
      setCancelTarget(null);
      setCancelReason('');
      await loadRegistrations(selectedEvent.id);
    } catch (e) {
      showErrorMsg(e.message || 'Failed to cancel registration.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportCSV = () => {
    const csv = exportRegistrants(registrations);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${selectedEvent?.title || 'event'}-registrants.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleOverrideCapacity = async () => {
    if (!newCapacity || !capacityReason.trim()) return;
    setActionLoading(true);
    try {
      await overrideCapacity(orgId, selectedEvent.id, parseInt(newCapacity, 10), adminId, capacityReason);
      showSuccessMsg(`Member capacity updated to ${newCapacity}.`);
      setShowCapacityModal(false);
      setNewCapacity('');
      setCapacityReason('');
      setSelectedEvent(p => ({ ...p, member_capacity: parseInt(newCapacity, 10) }));
    } catch (e) {
      showErrorMsg(e.message || 'Failed to override capacity.');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePromote = async () => {
    if (!promoteTarget || !promoteReason.trim()) return;
    setActionLoading(true);
    try {
      await promoteFromWaitlist(orgId, selectedEvent.id, promoteTarget.id, adminId, promoteReason);
      showSuccessMsg(`${promoteTarget.member_name} promoted to confirmed.`);
      setShowPromoteModal(false);
      setPromoteTarget(null);
      setPromoteReason('');
      await loadRegistrations(selectedEvent.id);
    } catch (e) {
      showErrorMsg(e.message || 'Failed to promote from waitlist.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManualAdd = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await manualAddRegistrant(
        orgId, selectedEvent.id,
        manualForm.memberId, manualForm.memberName, manualForm.memberEmail,
        adminId, manualForm.note
      );
      showSuccessMsg(`${manualForm.memberName} added successfully.`);
      setManualForm({ memberId: '', memberName: '', memberEmail: '', note: '' });
      await loadRegistrations(selectedEvent.id);
    } catch (e) {
      showErrorMsg(e.message || 'Failed to add registrant.');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmedRegs  = registrations.filter(r => r.status === 'CONFIRMED' || r.status === 'CHECKED_IN' || r.status === 'NO_SHOW');
  const cancelledRegs  = registrations.filter(r => r.status === 'CANCELLED');
  const waitlistedRegs = registrations.filter(r => r.status === 'WAITLISTED').sort((a, b) => (a.waitlist_position || 0) - (b.waitlist_position || 0));

  return (
    <div className="bb-prog-tool">
      <div className="bb-prog-tool-header">
        <h2>Registration &amp; Waitlist</h2>
        <p>Manage event registrations, waitlists, and manual additions.</p>
      </div>

      <TabBar activeTab={activeTab} onSelect={setActiveTab} />

      {error   && <div className="bb-alert bb-alert--error">{error}</div>}
      {success && <div className="bb-alert bb-alert--success">{success}</div>}

      {/* ── SELECT EVENT ── */}
      {activeTab === 'selectEvent' && (
        <div style={{ maxWidth: 600 }}>
          <h3 style={{ marginTop: 24, fontFamily: 'var(--bb-font-heading)', fontSize: 15, fontWeight: 700, color: '#0F766E', paddingLeft: '3ch' }}>Select an Event</h3>
          {eventsLoading ? (
            <div className="bb-loading-spinner" />
          ) : (
            <div className="bb-form-group">
              <label style={{ paddingLeft: '3ch' }}>Event</label>
              <select
                className="bb-select"
                value={selectedEvent?.id || ''}
                onChange={e => {
                  const evt = allEvents.find(ev => ev.id === e.target.value);
                  setSelectedEvent(evt || null);
                }}
              >
                <option value="">-- Select an event --</option>
                {allEvents.map(evt => (
                  <option key={evt.id} value={evt.id}>{evt.title} ({fmtDateTime(evt.start_datetime)})</option>
                ))}
              </select>
            </div>
          )}

          {selectedEvent && (
            <div style={{ background: '#F0FDFA', border: '1px solid #99F6E4', borderRadius: 8, padding: 16, marginTop: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#0F766E' }}>{selectedEvent.title}</div>
              <div style={{ display: 'flex', gap: 20, fontSize: 13, color: '#475569', flexWrap: 'wrap' }}>
                <span>Date: {fmtDateTime(selectedEvent.start_datetime)}</span>
                {selectedEvent.member_capacity != null && <span>Member Capacity: {selectedEvent.member_capacity}</span>}
                {selectedEvent.volunteer_capacity != null && <span>Volunteer Capacity: {selectedEvent.volunteer_capacity}</span>}
                <span>Confirmed: {counts.confirmed}</span>
                <span>Waitlisted: {counts.waitlisted}</span>
              </div>
              <button className="bb-btn-primary" style={{ marginTop: 12 }} onClick={handleLoadRegistrations}>
                Load Registrations
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── REGISTRATIONS ── */}
      {activeTab === 'registrations' && (
        <div>
          {!selectedEvent ? (
            <div className="bb-alert bb-alert--error">Please select an event first from the "Select Event" tab.</div>
          ) : (
            <>
              {/* ── Capacity summary ── */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {/* Member capacity row */}
                  <div style={{ display: 'flex', gap: 14, fontSize: 13, fontWeight: 600, flexWrap: 'wrap' }}>
                    <span style={{ color: '#6B46C1' }}>Members —</span>
                    <span style={{ color: '#059669' }}>Confirmed: {typeCounts.member_confirmed}</span>
                    {selectedEvent.member_capacity != null && (
                      <span style={{ color: '#6B7280' }}>/ Capacity: {selectedEvent.member_capacity}</span>
                    )}
                    <span style={{ color: '#D97706' }}>Waitlisted: {typeCounts.member_waitlisted}</span>
                  </div>
                  {/* Volunteer capacity row */}
                  {selectedEvent.volunteer_capacity != null && (
                    <div style={{ display: 'flex', gap: 14, fontSize: 13, fontWeight: 600, flexWrap: 'wrap' }}>
                      <span style={{ color: '#4338CA' }}>Volunteers —</span>
                      <span style={{ color: '#059669' }}>Confirmed: {typeCounts.volunteer_confirmed}</span>
                      <span style={{ color: '#6B7280' }}>/ Capacity: {selectedEvent.volunteer_capacity}</span>
                      <span style={{ color: '#D97706' }}>Waitlisted: {typeCounts.volunteer_waitlisted}</span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="bb-btn-outline-sm" onClick={handleExportCSV}>Export CSV</button>
                  <button className="bb-btn-outline-sm" onClick={() => { setNewCapacity(selectedEvent.member_capacity ?? ''); setShowCapacityModal(true); }}>
                    Override Capacity
                  </button>
                </div>
              </div>

              {regsLoading ? (
                <div className="bb-loading-spinner" />
              ) : confirmedRegs.length === 0 ? (
                <div className="bb-empty-state" style={{ padding: 32, textAlign: 'center', color: '#9CA3AF' }}>No registrations yet.</div>
              ) : (
                <table className="bb-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Event Change</th>
                      <th>Registered At</th>
                      <th>Photo Consent</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {confirmedRegs.map(reg => {
                      const isVolunteer = reg.registrant_type === 'volunteer';
                      return (
                        <tr key={reg.id}>
                          <td><strong>{reg.member_name}</strong></td>
                          <td>
                            <span className={isVolunteer ? 'bb-badge bb-badge--indigo' : 'bb-badge bb-badge--teal'}>
                              {isVolunteer ? 'Volunteer' : 'Member'}
                            </span>
                          </td>
                          <td>{reg.member_email}</td>
                          <td><span className={STATUS_BADGE[reg.status] || 'bb-badge bb-badge--grey'}>{reg.status}</span></td>
                          <td>
                            {reg.change_response_status === 'PENDING' && <span className="bb-badge bb-badge--orange" title={`Notified ${fmtDateTime(reg.change_notice?.notified_at)}`}>Awaiting reply</span>}
                            {reg.change_response_status === 'KEEP' && <span className="bb-badge bb-badge--green" title={`Replied ${fmtDateTime(reg.change_notice?.response_at)}`}>Keeping</span>}
                            {!reg.change_response_status && '—'}
                          </td>
                          <td>{fmtDateTime(reg.registered_at)}</td>
                          <td>{reg.photo_consent === true ? 'Yes' : reg.photo_consent === false ? 'No' : '—'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {reg.status === 'CONFIRMED' && (
                                <button className="bb-btn-sm" style={{ background: '#059669', color: '#fff' }}
                                  onClick={() => handleCheckIn(reg)} disabled={actionLoading}>
                                  Check In
                                </button>
                              )}
                              <button className="bb-btn-danger-sm" onClick={() => { setCancelTarget(reg); setShowCancelModal(true); }}>
                                Cancel
                              </button>
                              {reg.qr_code_token && (
                                <button className="bb-btn-outline-sm" onClick={() => { setQrTarget(reg); setShowQRModal(true); }}>
                                  QR
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {cancelledRegs.length > 0 && (
                <details style={{ marginTop: 20 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                    Cancelled registrations ({cancelledRegs.length})
                  </summary>
                  <table className="bb-table" style={{ marginTop: 8 }}>
                    <thead>
                      <tr><th>Name</th><th>Email</th><th>Cancelled At</th><th>Cancelled By</th><th>Reason</th></tr>
                    </thead>
                    <tbody>
                      {cancelledRegs.map(reg => (
                        <tr key={reg.id}>
                          <td>{reg.member_name}</td>
                          <td>{reg.member_email}</td>
                          <td>{fmtDateTime(reg.cancelled_at)}</td>
                          <td>{reg.cancelled_by === 'registrant' ? 'Registrant (via event-change notice)' : 'Admin'}</td>
                          <td>{reg.cancellation_reason || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </>
          )}
        </div>
      )}

      {/* ── WAITLIST ── */}
      {activeTab === 'waitlist' && (
        <div>
          {!selectedEvent ? (
            <div className="bb-alert bb-alert--error">Please select an event first from the "Select Event" tab.</div>
          ) : regsLoading ? (
            <div className="bb-loading-spinner" />
          ) : waitlistedRegs.length === 0 ? (
            <div className="bb-empty-state" style={{ padding: 32, textAlign: 'center', color: '#9CA3AF' }}>No one on the waitlist.</div>
          ) : (
            <table className="bb-table">
              <thead>
                <tr>
                  <th>Position</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Email</th>
                  <th>Event Change</th>
                  <th>Registered At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {waitlistedRegs.map(reg => {
                  const isVolunteer = reg.registrant_type === 'volunteer';
                  return (
                    <tr key={reg.id}>
                      <td><strong>#{reg.waitlist_position}</strong></td>
                      <td>{reg.member_name}</td>
                      <td>
                        <span className={isVolunteer ? 'bb-badge bb-badge--indigo' : 'bb-badge bb-badge--teal'}>
                          {isVolunteer ? 'Volunteer' : 'Member'}
                        </span>
                      </td>
                      <td>{reg.member_email}</td>
                      <td>
                        {reg.change_response_status === 'PENDING' && <span className="bb-badge bb-badge--orange">Awaiting reply</span>}
                        {reg.change_response_status === 'KEEP' && <span className="bb-badge bb-badge--green">Keeping</span>}
                        {!reg.change_response_status && '—'}
                      </td>
                      <td>{fmtDateTime(reg.registered_at)}</td>
                      <td>
                        <button className="bb-btn-sm" style={{ background: '#6B46C1', color: '#fff' }}
                          onClick={() => { setPromoteTarget(reg); setPromoteReason(''); setShowPromoteModal(true); }}>
                          Promote to Confirmed
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── MANUAL ADD ── */}
      {activeTab === 'manualAdd' && (
        <div style={{ maxWidth: 520 }}>
          <h3 style={{ marginTop: 0, fontFamily: 'var(--bb-font-heading)', fontSize: 15, fontWeight: 700, color: '#0F766E', paddingLeft: '3ch' }}>Manually Add Registrant</h3>
          {!selectedEvent ? (
            <div className="bb-alert bb-alert--error">Please select an event first from the "Select Event" tab.</div>
          ) : (
            <form onSubmit={handleManualAdd}>
              <div style={{ background: '#F0FDFA', border: '1px solid #99F6E4', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#134E4A' }}>
                Adding to: <strong>{selectedEvent.title}</strong>
              </div>
              <div className="bb-form-group">
                <label style={{ paddingLeft: '3ch' }}>Member ID <span style={{ color: '#DC2626' }}>*</span></label>
                <input className="bb-input" required value={manualForm.memberId} onChange={e => setManualForm(p => ({ ...p, memberId: e.target.value }))} placeholder="UID or member ID" />
              </div>
              <div className="bb-form-group">
                <label style={{ paddingLeft: '3ch' }}>Member Name <span style={{ color: '#DC2626' }}>*</span></label>
                <input className="bb-input" required value={manualForm.memberName} onChange={e => setManualForm(p => ({ ...p, memberName: e.target.value }))} placeholder="Full name" />
              </div>
              <div className="bb-form-group">
                <label style={{ paddingLeft: '3ch' }}>Member Email <span style={{ color: '#DC2626' }}>*</span></label>
                <input className="bb-input" required type="email" value={manualForm.memberEmail} onChange={e => setManualForm(p => ({ ...p, memberEmail: e.target.value }))} placeholder="email@example.com" />
              </div>
              <div className="bb-form-group">
                <label style={{ paddingLeft: '3ch' }}>Note</label>
                <textarea className="bb-textarea" rows={2} value={manualForm.note} onChange={e => setManualForm(p => ({ ...p, note: e.target.value }))} placeholder="Optional admin note..." />
              </div>
              <button className="bb-btn-primary" type="submit" disabled={actionLoading}>
                {actionLoading ? 'Adding...' : 'Add Registrant'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* ── Cancel Registration Modal ── */}
      {showCancelModal && cancelTarget && (
        <div className="bb-modal-overlay">
          <div className="bb-modal">
            <h3>Cancel Registration</h3>
            <p>Cancel <strong>{cancelTarget.member_name}</strong>'s registration? Please provide a reason:</p>
            <textarea
              className="bb-textarea"
              rows={3}
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Reason..."
              style={{ width: '100%', marginTop: 8 }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button className="bb-btn-outline-sm" onClick={() => { setShowCancelModal(false); setCancelTarget(null); setCancelReason(''); }}>Back</button>
              <button className="bb-btn-danger-sm" style={{ padding: '8px 16px' }} onClick={handleCancelReg} disabled={actionLoading || !cancelReason.trim()}>
                {actionLoading ? 'Cancelling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── QR Code Modal ── */}
      {showQRModal && qrTarget && (
        <div className="bb-modal-overlay">
          <div className="bb-modal" style={{ textAlign: 'center' }}>
            <h3>QR Code — {qrTarget.member_name}</h3>
            <img src={getQRCodeUrl(qrTarget.qr_code_token, 240)} alt="QR Code" style={{ display: 'block', margin: '16px auto', borderRadius: 8 }} />
            <p style={{ fontSize: 12, color: '#9CA3AF', wordBreak: 'break-all' }}>{qrTarget.qr_code_token}</p>
            <button className="bb-btn-outline-sm" onClick={() => { setShowQRModal(false); setQrTarget(null); }}>Close</button>
          </div>
        </div>
      )}

      {/* ── Override Capacity Modal ── */}
      {showCapacityModal && (
        <div className="bb-modal-overlay">
          <div className="bb-modal">
            <h3>Override Member Capacity</h3>
            <p>Current member capacity: <strong>{selectedEvent?.member_capacity ?? '—'}</strong></p>
            <div className="bb-form-group">
              <label>New Capacity</label>
              <input className="bb-input" type="number" min={1} value={newCapacity} onChange={e => setNewCapacity(e.target.value)} />
            </div>
            <div className="bb-form-group">
              <label>Reason <span style={{ color: '#DC2626' }}>*</span></label>
              <textarea className="bb-textarea" rows={2} value={capacityReason} onChange={e => setCapacityReason(e.target.value)} placeholder="Reason for override..." />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button className="bb-btn-outline-sm" onClick={() => { setShowCapacityModal(false); setNewCapacity(''); setCapacityReason(''); }}>Cancel</button>
              <button className="bb-btn-primary" onClick={handleOverrideCapacity} disabled={actionLoading || !capacityReason.trim() || !newCapacity}>
                {actionLoading ? 'Saving...' : 'Update Capacity'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Promote from Waitlist Modal ── */}
      {showPromoteModal && promoteTarget && (
        <div className="bb-modal-overlay">
          <div className="bb-modal">
            <h3>Promote from Waitlist</h3>
            <p>Promote <strong>{promoteTarget.member_name}</strong> to Confirmed? Please provide a reason:</p>
            <textarea
              className="bb-textarea"
              rows={2}
              value={promoteReason}
              onChange={e => setPromoteReason(e.target.value)}
              placeholder="Reason for manual promotion..."
              style={{ width: '100%', marginTop: 8 }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button className="bb-btn-outline-sm" onClick={() => { setShowPromoteModal(false); setPromoteTarget(null); setPromoteReason(''); }}>Cancel</button>
              <button className="bb-btn-primary" onClick={handlePromote} disabled={actionLoading || !promoteReason.trim()}>
                {actionLoading ? 'Promoting...' : 'Promote'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
